/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action, computed } from '@ember/object';
import { isNone } from '@ember/utils';
import { timeout } from 'ember-concurrency';
import config from 'cio/config/environment';
import BroadcastTriggerModel from 'cio/models/broadcast-trigger';
import { restartableTask } from 'ember-concurrency';
import ComposerEditableEntity from 'cio/utils/composer/composer-editable-entity';
import { ManualTrigger } from 'cio/components/action-composer';

const DEBOUNCE_DURATION = config.APP.debounceDuration;

interface MergeTriggerViewerArgs {
  editableEntity: ComposerEditableEntity;
  triggers: BroadcastTriggerModel[];
  selectedTrigger: BroadcastTriggerModel | ManualTrigger;
  isNewCampaign: boolean;
  isLiquidInsertionDisabled: boolean;
  canSelectPreviousTrigger: boolean;
  isTransactionalMessage: boolean;
  hasReadOnlyPermission: boolean;
  onInsertLiquid: (event: Event, value: string) => unknown;
  onSelectTrigger: (trigger: ManualTrigger | BroadcastTriggerModel | null) => unknown;
}

export default class MergeTriggerViewer extends Component<MergeTriggerViewerArgs> {
  @tracked editorContent = '';
  @tracked isEditorContentValid = true;
  @tracked isShowingEditor = false;

  constructor(owner: unknown, args: MergeTriggerViewerArgs) {
    super(owner, args);

    let { selectedTrigger } = this.args;

    if (selectedTrigger && selectedTrigger instanceof ManualTrigger) {
      this.editorContent = selectedTrigger.triggerData;
      this.isShowingEditor = true;
    }
  }

  get canSelectPreviousTrigger() {
    return this.args.canSelectPreviousTrigger ?? true;
  }

  @computed('args.selectedTrigger')
  get isManualData(): boolean {
    return this.args.selectedTrigger instanceof ManualTrigger;
  }

  @computed('args.selectedTrigger')
  get triggerAttrs() {
    let attributes = this.args.selectedTrigger?.triggerData;
    let parsed: Record<string, any>;

    try {
      parsed = JSON.parse(attributes);
    } catch (e) {
      return [];
    }

    if (isNone(attributes) || isNone(parsed)) {
      return [];
    }

    return Object.keys(parsed).map((key: string) => {
      return {
        label: key,
        value: parsed[key],
      };
    });
  }

  editorContentChange = restartableTask(async (triggerData: string) => {
    await timeout(DEBOUNCE_DURATION);

    let trigger = new ManualTrigger(triggerData);

    this.args.onSelectTrigger(trigger);
  });

  @action
  triggerSelected(trigger: BroadcastTriggerModel) {
    if (this.isShowingEditor) {
      this._hideEditor();
    }

    this.args.onSelectTrigger(trigger);
  }

  @action
  showEditor() {
    this.args.onSelectTrigger(null);
    this.isShowingEditor = true;
  }

  @action
  hideEditor() {
    this.args.onSelectTrigger(null);
    this._hideEditor();
  }

  private _hideEditor() {
    this.isShowingEditor = false;
    this.editorContent = '';
  }
}
